import { useCallback, useEffect, useMemo, useState } from 'react'
import { usePayItemSummaryQuery } from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import {
  Group,
  Internal,
  Item,
  PayItemsModuleContext,
  WorkflowConnection,
} from './types'
import { InternalPayItemConnectionClassType } from '@cims-online/shared'
import { mergeWorkflowConnections, sortByOrder } from './utils'
import { useProjectContext } from 'context/ProjectContext'

const { Provider, useContextFn } = createStrictContext<PayItemsModuleContext>(
  'PayItemsModuleContext'
)

export const usePayItemsModuleContext = useContextFn

const PayItemsModuleProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const {
    data,
    loading: fetching,
    refetch,
  } = usePayItemSummaryQuery({
    ...DefaultQueryHookOptions,
    variables: { projectId },
  })
  /**
   * we have to overwrite projectToInternalConnections because there's a chance
   * a new internal connection hasn't been configured yet
   */
  const [p2ic, setP2ic] = useState(Array<Internal>())
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
    setP2ic(
      InternalPayItemConnectionClassType.values.map((internal) => {
        const found = data?.project?.projectToInternalConnections?.find(
          ({ internalConnectionId }) => internalConnectionId === internal.id
        ) ?? {
          __typename: 'ProjectToInternalConnection',
          groupId: null,
          internalConnectionId: internal.id,
          order: 0,
        }
        return { internal, ...found }
      })
    )
  }, [data?.project?.projectToInternalConnections])
  const workflowConnections = useMemo(
    () =>
      mergeWorkflowConnections(
        data?.project?.workflows.records ?? [],
        data?.project?.workflowPayItemSummaryConnections ?? []
      ),
    [
      data?.project?.workflowPayItemSummaryConnections,
      data?.project?.workflows.records,
    ]
  )
  const internals = useCallback(
    (group?: Omit<Group, 'children'>) =>
      p2ic.reduce((internals, internal) => {
        if (group && internal.groupId === group.id) internals.push(internal)
        else if (!group && !internal.groupId) internals.push(internal)
        return internals
      }, Array<Internal>()),
    [p2ic]
  )
  const workflows = useCallback(
    (group?: Omit<Group, 'children'>) =>
      workflowConnections.reduce((workflows, workflow) => {
        if (group && workflow.groupId === group.id) workflows.push(workflow)
        else if (!group && !workflow.groupId) workflows.push(workflow)
        return workflows
      }, Array<WorkflowConnection>()),
    [workflowConnections]
  )
  const [sorted, setSorted] = useState(Array<Item>())
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
    setSorted(() =>
      [
        ...(data?.project?.payItemConnectionGroups ?? []).map<Group>(
          (group) => {
            const children = [
              ...(data?.project?.payItemConnections ?? []).filter(
                ({ groupId }) => groupId === group.id
              ),
              ...internals(group),
              ...workflows(group),
            ].sort(sortByOrder)
            return { ...group, children }
          }
        ),
        ...(data?.project?.payItemConnections ?? []).filter(
          ({ groupId }) => !groupId
        ),
        ...internals(),
        ...workflows(),
      ].sort(sortByOrder)
    )
  }, [
    data?.project?.payItemConnectionGroups,
    data?.project?.payItemConnections,
    internals,
    projectId,
    workflows,
  ])
  return (
    <Provider value={{ fetching, refetch, sorted, workflowConnections }}>
      {children}
    </Provider>
  )
}

export default PayItemsModuleProvider
